<template>
  <div class="va-dropdown-search">
    <va-popup right :opened="searchResultPopupOpened" @blur="onPopupClose">
      <template #toggler>
        <div
          ref="search-field-container"
          data-testid="filter-search-container"
          class="search-field-container"
          aria-haspopup="listbox"
          aria-expanded="false"
          @keydown.down.prevent
          @keydown="onKeydown"
          @click="setFocusOnInput"
        >
          <div
            v-for="selectedSearchText in selectedSearchTexts"
            :key="selectedSearchText.id"
            class="search-text-container"
          >
            <div class="search-text">
              <span class="break-word">
                {{ selectedSearchText.displayName }}
              </span>
              <svg-icon
                icon="close"
                data-testid="dropdown-search--remove-item"
                class="close-icon"
                @click="onRemoveOption(selectedSearchText.id)"
              />
            </div>
          </div>
          <input
            ref="input-area"
            v-model="searchText"
            data-testid="input-area"
            class="input-area"
          />
          <span
            v-if="
              placeholder && !searchText && selectedSearchTexts.length === 0
            "
            class="placeholder"
          >
            {{ placeholder }}
          </span>
          <svg-icon
            v-if="selectedSearchTexts.length === 0 && optionsToShow.length > 0"
            icon="search"
            class="search-icon"
            @click="searchResultPopupOpened = true"
          />
        </div>
      </template>
      <div ref="popup-content" class="search-result-container">
        <ul role="listbox">
          <li
            v-for="(dropdownOption, index) in optionsToShow"
            :id="`option${index}`"
            :key="dropdownOption.id"
            tabindex="-1"
            :data-testid="`search-result-container-${dropdownOption.id}`"
            class="noselect"
            role="option"
            :title="dropdownOption.displayName"
            @keydown.prevent="onListKeydown($event, dropdownOption)"
            @click="onOptionClick(dropdownOption)"
          >
            {{ dropdownOption.displayName }}
          </li>
        </ul>
      </div>
    </va-popup>
  </div>
</template>

<script>
import VaPopup from '@/components/framework/vaPopup/VaPopup.vue';
import SvgIcon from '@/components/icons/SvgIcon.vue';

export default {
  components: {
    VaPopup,
    SvgIcon,
  },
  props: {
    placeholder: {
      type: String,
      default: '',
    },
    dropdownOptions: {
      type: Array,
      default() {
        return [];
      },
    },
    initialValues: {
      type: Array,
      default() {
        return [];
      },
    },
    singleSelect: {
      type: Boolean,
    },
  },
  emits: ['selection_updated'],
  data() {
    return {
      searchText: '',
      searchResultPopupOpened: false,
    };
  },
  computed: {
    optionsToShow() {
      let options;
      if (!this.singleSelect) {
        options = this.dropdownOptions.filter((f) => {
          return !this.selectedSearchTexts
            .map((sel) => {
              return sel.id;
            })
            .includes(f.id);
        });
      } else if (this.singleSelect) {
        options = this.dropdownOptions;
      }
      return options.filter((f) =>
        f.displayName.toLowerCase().includes(this.searchText.toLowerCase()),
      );
    },
    selectedSearchTexts() {
      return this.dropdownOptions.filter((f) => {
        return this.initialValues.includes(f.id);
      });
    },
  },
  methods: {
    setFocusOnInput(e) {
      if (
        this.optionsToShow.length > 0 &&
        (e.target === this.$refs['search-field-container'] ||
          e.target === this.$refs['input-area'])
      ) {
        this.searchResultPopupOpened = true;
        this.$refs['input-area'].focus();
      }
    },
    onKeydown(e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        this.searchResultPopupOpened = true;
        this.$nextTick(() => {
          document.getElementById('option0').focus();
        });
      } else if (
        e.key !== 'Escape' &&
        e.key !== 'Tab' &&
        e.key !== 'Shift' &&
        this.optionsToShow.length
      ) {
        this.searchResultPopupOpened = true;
      }
    },
    onPopupClose() {
      this.searchText = '';
      this.searchResultPopupOpened = false;
    },
    onListKeydown(e, option) {
      if (e.key === 'ArrowDown') {
        const activeOption = document.activeElement;
        const nextOption = activeOption.nextElementSibling;
        if (nextOption) {
          nextOption.focus();
        }
      } else if (e.key === 'ArrowUp') {
        const activeOption = document.activeElement;
        const prevOption = activeOption.previousElementSibling;
        if (prevOption) {
          prevOption.focus();
        }
      } else if (e.key === 'Enter') {
        this.onOptionClick(option);
      } else if (e.key === 'Escape' || e.key === 'Tab') {
        this.onPopupClose();
      }
    },
    onOptionClick(option) {
      let selectedSearchTexts = this.selectedSearchTexts;
      if (this.singleSelect) {
        selectedSearchTexts = [option];
        this.searchResultPopupOpened = false;
        this.searchText = '';
      } else {
        selectedSearchTexts.push(option);
        this.$refs['input-area'].focus();
      }
      this.$emit('selection_updated', selectedSearchTexts);
    },
    onRemoveOption(id) {
      const searchTextToRemove = this.selectedSearchTexts.find(
        (searchText) => searchText.id === id,
      );
      if (searchTextToRemove) {
        let selectedSearchTexts = this.selectedSearchTexts;
        selectedSearchTexts.splice(
          selectedSearchTexts.indexOf(searchTextToRemove),
          1,
        );
        this.$emit('selection_updated', selectedSearchTexts);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.va-dropdown-search {
  display: inline-block;
  color: $color-text-secondary;
  width: 100%;
  font-size: 14px;

  .break-word {
    word-break: break-all;
  }

  .search-text-container {
    display: flex;
    align-items: center;

    .search-text {
      background-color: $color-fill-inverted;
      border: 1px solid $color-border-subtle;
      padding: 10px 15px;
      border-radius: 15.8px;
      font-size: 0.75rem;
      text-align: center;
      margin: 2.5px;
      line-height: 1;

      .close-icon {
        padding-left: 0.5rem;
        transition: color 0.2s ease-out;
        font-size: 0.5rem;

        &:hover {
          color: $color-text;
        }
      }
    }
  }

  .search-field-container {
    position: relative;
    min-height: 40px;
    width: 100%;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 2.5px;
    background-color: $color-input;
    border: 1px solid $color-input-border;
    border-radius: 0.25rem;
    cursor: pointer;

    &:hover {
      background-color: $color-input-hover;
      border-color: $color-input-border-hover;
    }

    &:focus-within {
      background-color: $color-input-active;
      border-color: $color-input-border-active;
    }

    .input-area {
      border: none;
      background: none;
      color: $color-text;
      margin: 2.5px 7.5px;
      cursor: pointer;
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;

      &:focus {
        outline: none;
      }
    }

    .search-icon {
      position: absolute;
      right: 12px;
      top: 12px;
      font-size: 1rem;
    }

    .placeholder {
      position: absolute;
      font-size: 14px;
      padding-left: 9.5px;
      user-select: none;
      pointer-events: none;
      color: $color-text-secondary;
    }
  }

  .search-result-container {
    background-color: $color-bg-tertiary;
    right: 0;
    border: 1px solid $color-input-border;
    border-radius: 0.25rem;
    box-shadow: 0 3px 10px $color-shadow;

    ul {
      max-height: 240px;
      overflow-y: scroll;
      margin: 0;
      padding: 0;
      border-radius: 4px;

      li {
        height: 40px;
        padding: 0 12px;
        width: 200px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 40px;
        transition:
          background-color 0.2s ease-out,
          color 0.2s ease-out;

        &:focus-visible {
          outline: $color-border-focus solid 1px;
        }

        &:focus,
        &:hover {
          background-color: $color-bg-tertiary-hover;
          color: $color-text;
          cursor: pointer;
        }
      }
    }
  }

  .tag-color {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    margin: 1px 6px 1px 1px;
  }

  .separator {
    text-transform: lowercase;
    line-height: 1;
  }
}
</style>
